'use client';

import React from 'react';

import { DayPicker, type MonthProps } from 'react-day-picker';
import { nl, fr, enUS } from 'react-day-picker/locale';
import styles from './calendar.module.scss';
import Icon from '../Icon/Icon';
import { Button } from '../Button/Button';
import { useTranslation } from 'utils/translation';

type CalendarProps = React.ComponentProps<typeof DayPicker>;

const locales = {
  nl,
  fr,
  en: enUS,
} as const;

function navClassName(node: React.ReactNode): string {
  if (!React.isValidElement(node)) {
    return '';
  }

  const cn = (node.props as { className?: string }).className;

  return cn ?? '';
}

/**
 * react-day-picker puts Prev / MonthCaption / Next before MonthGrid.
 * With multiple months, either Prev or Next may be omitted.
 */
function partitionNavSlots(navNodes: React.ReactNode[]): {
  prev: React.ReactNode;
  caption: React.ReactNode;
  next: React.ReactNode;
} {
  let prev: React.ReactNode = null;
  let caption: React.ReactNode = null;
  let next: React.ReactNode = null;

  for (const node of navNodes) {
    const cls = navClassName(node);
    if (cls.includes('rdp-button_previous')) {
      prev = node;
    } else if (cls.includes('rdp-button_next')) {
      next = node;
    } else if (cls.includes('rdp-month_caption')) {
      caption = node;
    }
  }

  if (caption === null && navNodes.length === 3) {
    prev = navNodes[0];
    caption = navNodes[1];
    next = navNodes[2];
  } else if (caption === null && navNodes.length === 2) {
    const a = navClassName(navNodes[0]);
    if (a.includes('rdp-button_previous')) {
      prev = navNodes[0];
      caption = navNodes[1];
    } else {
      caption = navNodes[0];
      next = navNodes[1];
    }
  } else if (caption === null && navNodes.length === 1) {
    caption = navNodes[0];
  }

  return { prev, caption, next };
}

function CalendarMonth({
  className,
  style,
  calendarMonth: _calendarMonth,
  displayIndex: _displayIndex,
  children,
  ...divProps
}: MonthProps) {
  const childrenArray = React.Children.toArray(children);
  const grid =
    childrenArray.length > 0 ? childrenArray[childrenArray.length - 1] : null;
  const navNodes = childrenArray.slice(0, -1);
  const { prev, caption, next } = partitionNavSlots(navNodes);

  return (
    <div className={className} style={style} {...divProps}>
      <div className={styles.monthNav}>
        <div className={styles.monthNavStart}>{prev}</div>
        <div className={styles.monthNavCaption}>{caption}</div>
        <div className={styles.monthNavEnd}>{next}</div>
      </div>
      {grid}
    </div>
  );
}

function Calendar({ className, classNames, ...props }: CalendarProps) {
  const { i18n } = useTranslation();

  return (
    <DayPicker
      {...props}
      navLayout={props.navLayout ?? 'around'}
      className={styles.calendar}
      locale={locales[i18n.language as keyof typeof locales] || locales.en}
      classNames={{
        months: styles.months,
        month: styles.month,
        caption: styles.caption,
        weekday: styles.weekday,
        caption_label: styles.captionLabel,
        nav: styles.nav,
        nav_button: styles.navButton,
        nav_button_previous: styles.navButtonPrev,
        nav_button_next: styles.navButtonNext,
        table: styles.table,
        head_row: styles.headRow,
        head_cell: styles.headCell,
        row: styles.row,
        cell: styles.cell,
        day: styles.day,
        day_range_start: styles.cellRangeStart,
        day_range_end: styles.cellRangeEnd,
        selected: styles.daySelected,
        day_today: styles.dayToday,
        day_outside: styles.dayOutside,
        day_disabled: styles.dayDisabled,
        day_range_middle: styles.dayRangeMiddle,
        day_hidden: styles.dayHidden,
        day_button: styles.dayButton,
      }}
      modifiersClassNames={{
        booked: styles.dayBooked,
        ...props.modifiersClassNames,
      }}
      components={{
        Month: CalendarMonth,
        PreviousMonthButton: ({ className, ...props }: any) => (
          <Button size="xxs" variant="icon" className={className} {...props}>
            <Icon name="fa-regular fa-chevron-left" />
          </Button>
        ),
        NextMonthButton: ({ className, ...props }: any) => (
          <Button size="xxs" variant="icon" className={className} {...props}>
            <Icon name="fa-regular fa-chevron-right" />
          </Button>
        ),
      }}
    />
  );
}

export default Calendar;
